import { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import PAGE_SEO from "../data/pageSeo.json";
import { CatalogItem } from "./CatalogItem";
import { useScrollReveal } from "../utils/useScrollReveal";
import { readEmbedded } from "../utils/embedded";
import firebase from "../components/firebase";
import "./firebase";
import "../styles/catalog.css";

const ITEMS_PER_PAGE = 12;

export const Catalog = () => {
  // Започва от вградения в страницата списък (src/utils/embedded.js), за да
  // не е празна мрежата, докато Firestore отговори.
  const [catalog, setCatalog] = useState(() => readEmbedded('sa-products') || []);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const topRef = useRef(null);

  // Re-run reveal observer whenever page or data changes
  useScrollReveal([currentPage, catalog]);

  // Scroll to top of page when user changes page
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);


const filteredCatalog = catalog.filter((product) => {
  const query = searchQuery.toLowerCase();
  const nameMatch = product.name?.toLowerCase().includes(query);
  const catalogMatch = product.catalogNumber && product.catalogNumber.toLowerCase().includes(query);
  return nameMatch || catalogMatch;
});

const totalPages = Math.ceil(filteredCatalog.length / ITEMS_PER_PAGE);
const paginatedCatalog = filteredCatalog.slice(
  (currentPage - 1) * ITEMS_PER_PAGE,
  currentPage * ITEMS_PER_PAGE
);

const handleSearchChange = (e) => {
  setSearchQuery(e.target.value);
  setCurrentPage(1);
};

  useEffect(() => {
    const db = firebase.firestore();

    const fetchCatalog = async () => {
      try {
        const snapshot = await db.collection("Products").get();
        const catalogData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        // Празен отговор (без връзка) не изтрива вградения списък — вж. App.js.
        if (catalogData.length) {
          setCatalog(catalogData);
          localStorage.setItem("catalog", JSON.stringify(catalogData));
        }
      } catch (error) {
        console.log("Error fetching catalog:", error);
      }
    };

    fetchCatalog();
  }, []);

  const handleDeleteClick = async (prodId) => {
    const db = firebase.firestore();
    try {
      await db.collection("Products").doc(prodId).delete();
      alert("Product deleted successfully!");

      // Get the updated catalog from Firebase after deleting the product
      const snapshot = await db.collection("Products").get();
      const catalogData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setCatalog(catalogData);

      // Update the local storage version of the catalog
      localStorage.setItem("catalog", JSON.stringify(catalogData));
    } catch (error) {
      console.log("Error deleting product:", error);
      alert("Error deleting product. Please try again.");
    }
  };

  return (
    <>
    <Helmet>
      <title>{PAGE_SEO.catalog.title}</title>
      <meta name="description" content={PAGE_SEO.catalog.description} />
      <link rel="canonical" href="https://stamsyart.bg/catalog" />
    </Helmet>
    <div className="catalog-container" ref={topRef}>
      <div className="catalog-wrapper">
        <div className="catalog-main">
          <div className="catalog-breadcrumb">
            <a href="/">Начало</a>
            <span>/</span>
            <span>Каталог</span>
          </div>

          <div style={{ marginBottom: 16 }}>
            <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 6 }}>Ръчно рисувани дрехи и подаръци</h1>
            <p style={{ color: '#666', fontSize: 14, margin: 0 }}>
              Ръчно рисувани тениски, пътечки за прощъпулник, персонализирани чаши с лик, детски дрехи с анимационни герои, коледни подаръци и handmade сувенири от Пазарджик. Всеки продукт е уникален, изработен с любов и доставен до Еконт или Спиди в цяла България.
            </p>
          </div>

          <div className="catalog-search-container">
            <input
              type="text"
              className="catalog-search-input"
              placeholder="Търсене на продукти..."
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </div>
          
          <div className="product-grid">
            {filteredCatalog.length === 0 && (
              <div className="empty-catalog">
                <h3>Няма намерени продукти</h3>
              </div>
            )}
            {paginatedCatalog.map((product, idx) => (
              <div
                key={product.id}
                className="reveal-up"
                style={{ transitionDelay: `${(idx % 4) * 0.09}s` }}
              >
                <CatalogItem
                  {...product}
                  handleDeleteClick={handleDeleteClick}
                />
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <nav className="catalog-pages" aria-label="Страници на каталога">
              <button
                type="button"
                className="catalog-page catalog-page--step"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                ← Предишна
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(page =>
                  page === 1 || page === totalPages ||
                  (page >= currentPage - 2 && page <= currentPage + 2)
                )
                .reduce((acc, page, idx, arr) => {
                  if (idx > 0 && page - arr[idx - 1] > 1) acc.push('...');
                  acc.push(page);
                  return acc;
                }, [])
                .map((item, idx) =>
                  item === '...' ? (
                    <span key={`ellipsis-${idx}`} className="catalog-pages-gap" aria-hidden="true">…</span>
                  ) : (
                    <button
                      type="button"
                      key={item}
                      className={`catalog-page${item === currentPage ? ' is-current' : ''}`}
                      onClick={() => setCurrentPage(item)}
                      aria-current={item === currentPage ? 'page' : undefined}
                      aria-label={`Страница ${item}`}
                    >
                      {item}
                    </button>
                  )
                )}

              <button
                type="button"
                className="catalog-page catalog-page--step"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Следваща →
              </button>
            </nav>
          )}
        </div>
      </div>
    </div>
    </>
  );
};
